import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import './Sidebar.css';
import { SidebarProps } from '../../types/components/sidebar';
import MenuContainer from '../menu/MenuContainer';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isAreaCalcControlOpenAtom, dataLoadedResultAtom, isDistanceControlOpenAtom, isDrawControlOpenAtom, isLegendControlOpenAtom, isSeparateDisplayControllerAtom, isSidebarOpenAtom, menuAtom, menuItemsAtom, selectedBaseMapLeftAtom, selectedLayersAtom, selectedMapIdAtom, sidebarHeightAtom, selectedSubMapLayersAtom, selectedThemeMapAtom, /* selectedMapIdAtom */ } from '../../atoms';
import { emptySelectedLayer } from '../../lib/selectedLayersControl';
import MapToggleBtns, { MapToggleBtnsColorType } from '../mapToggleBtns/MapToggleBtns';
import { MAIN_MAP_ID, SECOND_MAP_ID } from '../../constants/components/map';
import SquareButton from '../button/SquareButton';
import { useSearchParams } from 'react-router-dom';
import { BsCardList, BsCaretLeftFill, BsCaretRightFill, BsChevronCompactDown, BsChevronCompactUp, BsDashSquareDotted, BsXLg } from 'react-icons/bs';
import { anyType, mapType } from '../../types';
import mapStyleConfig from '../../assets/mapStyleConfig.json';
import ReactGA from 'react-ga4';
import { findGyoseikaiObject } from '../../lib/objectHighlight';
import Loading from '../loading/Loading';
import Error from '../loading/Error';


export type mapToggleBtnsDataType = { 
  label: string;
  value: mapType;
  color?: MapToggleBtnsColorType;
  selectedColor?: MapToggleBtnsColorType;
}[];


const toggleButtons: mapToggleBtnsDataType = [
  { label: '左の地図', value: SECOND_MAP_ID, color: 'light-blue' as MapToggleBtnsColorType, selectedColor: 'blue' as MapToggleBtnsColorType },
  { label: '右の地図', value: MAIN_MAP_ID, color: 'light-red' as MapToggleBtnsColorType, selectedColor: 'red' as MapToggleBtnsColorType },
]


const Sidebar = (props: SidebarProps) => {
  const { siteName, isDesktop } = props;

  const [mapId, setMapId] = useState<mapType>(MAIN_MAP_ID);

  const [selectedLayers, setSelectedLayers] = useAtom(selectedLayersAtom);
  const [selectedSubLayers, setSelectedSubLayers] = useAtom(selectedSubMapLayersAtom);

  const setSelectedThemeMap = useSetAtom(selectedThemeMapAtom);    // 選択中のテーマ図

  const [isSeparateDisplay, setIsSeparateDisplay] = useAtom(isSeparateDisplayControllerAtom);
  const [selectedMapId, setSelectedMapId] = useAtom(selectedMapIdAtom);
  const menu = useAtomValue(menuAtom);
  const [sidebarHeight, setSidebarHeight] = useAtom(sidebarHeightAtom);       // サイドバー開け閉め（スマホ）
  const [isSidebarOpen, setIsSidebarOpen] = useAtom(isSidebarOpenAtom);       // サイドバー開け閉め（PC）
  const dataLoadedResult = useAtomValue(dataLoadedResultAtom);                        // データ読み込み完了フラグ
  const menuItems = useAtomValue(menuItemsAtom);

  const setIsDistanceControlOpen = useSetAtom(isDistanceControlOpenAtom);
  const setIsAreaCalcControlOpen = useSetAtom(isAreaCalcControlOpenAtom);
  const setIsDrawControlOpen = useSetAtom(isDrawControlOpenAtom);
  const setSelectedBaseMapLeft = useSetAtom(selectedBaseMapLeftAtom);
  const [isLegendControlOpen, setIsLegendControlOpen] = useAtom(isLegendControlOpenAtom);


  // URL
  const setSearchParams = useSearchParams()[1];

  const startY = useRef<number | undefined>(undefined);
  const isScrollingContent = useRef(false);


  /* 凡例 */
  const openLegend = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsLegendControlOpen(!isLegendControlOpen);
    if(!isLegendControlOpen) {
      //【GoogleAnalytics】凡例ボタン クリックトラッキング
      ReactGA.event({
        category: "button_click", // カテゴリ（任意の名前）
        action: "click",          // イベントの種類
        label: 'メニュー左/凡例',       // ボタン名
      });
    }
  };

  /**********************
   * 2画面表示時の処理
   **********************/
  const onClickSeparateDisplay = useCallback(() => {
    setIsSeparateDisplay(!isSeparateDisplay);

    if (!isSeparateDisplay) {
      setSelectedBaseMapLeft(mapStyleConfig[0]);
      setIsDistanceControlOpen(undefined);
      setIsAreaCalcControlOpen(undefined);
      setIsDrawControlOpen(undefined);

      //【GoogleAnalytics】2画面 クリックトラッキング
      ReactGA.event({
        category: "button_click", // カテゴリ（任意の名前）
        action: "click",          // イベントの種類
        label: 'メニュー左/2画面',       // ボタン名
      });
    }
  }, [isSeparateDisplay]);


  const onClickPhoneIcon = (e: anyType) => {
    e.stopPropagation();
    // タップと判断
    if (sidebarHeight === 'close') {
      setSidebarHeight('half');
    } else if (sidebarHeight === 'half') {
      setSidebarHeight('full');
    } else if (sidebarHeight === 'full') {
      setSidebarHeight('half');
    }
  };

  /* ****************
   * 選択中のレイヤーを全解除
   * ****************/
  const selectAllRelease = () => {
    const gyoseikaiObject: anyType = findGyoseikaiObject(menuItems);
    
    if(selectedMapId !== MAIN_MAP_ID ) { 
      setSelectedSubLayers(emptySelectedLayer(selectedSubLayers));
      return;
    }

    setSelectedLayers(emptySelectedLayer(selectedLayers));
    setSelectedThemeMap(null);

    // URLのレイヤーを更新
    if(gyoseikaiObject.shortId) {
      setSearchParams((prev) => {
        prev.set('layers', gyoseikaiObject?.shortId);
        return prev;
      });
    }
  }

  /* ****************
   * メニューを閉じる（PC：左右）
   * ****************/
  const closeSideMenuHandler = useCallback<React.MouseEventHandler<HTMLDivElement>>((event) => {
    setIsSidebarOpen(!isSidebarOpen);
    event.stopPropagation();
  }, [isSidebarOpen, setIsSidebarOpen]);

  /* ****************
   * タッチ開始
   * ****************/
  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    startY.current = e.touches[0].clientY;
    // スクロール開始時の位置を記録
    const target = e.target as HTMLElement;
    // .sidebar-inner またはその子要素かどうかを判定
    const isInnerContent = target.closest('.sidebar-inner');
    isScrollingContent.current = !!isInnerContent;
  };

  /* ****************
   * タッチ終了（表示）
   * ****************/
  const handleTouchEnd = (e: React.TouchEvent) => {
    e.stopPropagation();
    if(!startY.current) { return; }
    
    const endY = e.changedTouches[0].clientY;
    const diffY = startY.current - endY;
  
    if (Math.abs(diffY) < 20) {
      return;
    }
    if (diffY > 0) {
      // 上方向にスワイプ
      if (sidebarHeight === 'close') {
        setSidebarHeight('half');
      } else if (sidebarHeight === 'half') {
        setSidebarHeight('full');
      }
    } else {
      // 下方向にスワイプ
      if (sidebarHeight === 'full') {
        setSidebarHeight('half');
      } else if (sidebarHeight === 'half') {
        setSidebarHeight('close');
      }
    }

    startY.current = undefined;
  };

  useEffect(() => {
    if (isDesktop) { setSidebarHeight('full'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDesktop]);

  /* ****************
   * 2画面表示じゃなかったら、メインの地図を選択
   * ****************/
  useEffect(() => {
    if (isSeparateDisplay) { return; }
    setMapId(MAIN_MAP_ID);
  }, [isSeparateDisplay]);

  /* ****************
   * 選択中のマップIDを更新
   * ****************/
  useEffect(() => {
    setSelectedMapId(mapId);
  }, [mapId]);


  return (
    <>
      {!isSidebarOpen &&
        <div className={classNames('toggle-btn', 'open-btn')} onClick={closeSideMenuHandler}>
          <BsCaretRightFill />
        </div>
      }
      <div
        className={
          classNames('sidebar',
            {
              'sidebar-half': sidebarHeight === 'half',
              'sidebar-full': sidebarHeight === 'full',
              'sidebar-hide': !isSidebarOpen,
              'menu-reduce': isSeparateDisplay
            }
          )
        }
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className={classNames('toggle-btn', 'close-btn')} onClick={closeSideMenuHandler}><BsCaretLeftFill /></div>
        <div className='sidebar-inner'>
          {/* スマホ用アイコン */}
          { (!isDesktop && sidebarHeight === 'close') && <BsChevronCompactUp className='sidebar-phone-icon' onClick={onClickPhoneIcon}/> }
          { (!isDesktop && sidebarHeight === 'half') && <div className='sidebar-phone-icon line' onClick={onClickPhoneIcon}></div> }
          { (!isDesktop && sidebarHeight === 'full') && <BsChevronCompactDown className='sidebar-phone-icon' onClick={onClickPhoneIcon}/> }
          <h2>
            <picture>
              <source media="(max-width:768px)" srcSet="yaizu-logo-circle.png" width="30" height="30"/>
              <img src='yaizu-logo-circle.png' alt="logo" width="35" height="35"/>
            </picture>
            {siteName}
          </h2>

          {isDesktop &&
            <div className='dual-screen-button-wrapper'>
              <SquareButton
                label={isSeparateDisplay ? '2画面解除' : '2画面表示'}
                onClick={onClickSeparateDisplay}
                small
                bold
                color='secondary'
                icon={isSeparateDisplay && <BsXLg />}
                selected={isSeparateDisplay}
              />
              {isSeparateDisplay &&
                <div className='toggle-btn-container'>
                  <MapToggleBtns dataList={toggleButtons} value={mapId} setValue={setMapId} small />
                </div>
              }
            </div>
          }

          { /* データローディング中 */
            dataLoadedResult === 'loading' && <Loading />
          }
          { /* データセットエラー */
            (typeof dataLoadedResult === 'object' && dataLoadedResult.result === 'error') && <Error />
          }
          { /* データセット成功 */
            (typeof dataLoadedResult === 'object' && dataLoadedResult.result === 'success') &&
            <div className='menu-container'>
              <div className='menu-btn-container'>
                <SquareButton
                  label='全データ選択解除'
                  onClick={selectAllRelease}
                  small bold color='secondary'
                  icon={<BsDashSquareDotted />}
                />
                <SquareButton
                  label='凡例を見る'
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => openLegend(e)}
                  small bold color='secondary'
                  icon={<BsCardList />}
                  selected={isLegendControlOpen}
                />
              </div>
              <div className='catalog-container' onTouchStart={(e) => e.stopPropagation()}>
              { menu &&
                  Object.entries(menu).map(([sectionName, menu]) => {
                    return <MenuContainer
                      key={sectionName}
                      sectionName={sectionName}
                      menu={menu}
                      selectedLayers={ selectedMapId === MAIN_MAP_ID ? selectedLayers : selectedSubLayers }
                      setSelectedLayers={ selectedMapId === MAIN_MAP_ID ? setSelectedLayers : setSelectedSubLayers }
                    />
                  })
              }
              </div>
            </div>
          }

        </div>
      </div>
    </>
  );
};

export default Sidebar;
